import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Housing', 'Utilities', 'Transportation', 'Insurance', 
  'Groceries', 'Entertainment', 'Subscriptions', 'Other'
];

function HouseholdBills() {
  const { householdId } = useParams();
  const [bills, setBills] = useState([]);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newBill, setNewBill] = useState({
    name: '',
    amount: '',
    category: 'Other',
    due_day: 1,
    frequency: 'Monthly',
  });

  const loadData = useCallback(async () => {
    try {
      const [billsRes, householdRes] = await Promise.all([
        fetch(`${API}/households/${householdId}/bills`, { credentials: 'include' }),
        fetch(`${API}/households/${householdId}`, { credentials: 'include' })
      ]);
      
      if (billsRes.ok) {
        const data = await billsRes.json();
        setBills(data);
      }
      
      if (householdRes.ok) {
        const data = await householdRes.json();
        setHousehold(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddBill = async () => {
    if (!newBill.name || !newBill.amount) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/households/${householdId}/bills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newBill,
          amount: parseFloat(newBill.amount),
          due_day: parseInt(newBill.due_day),
        }),
      });
      
      if (response.ok) {
        setNewBill({ name: '', amount: '', category: 'Other', due_day: 1, frequency: 'Monthly' });
        setShowAddForm(false);
        loadData();
      }
    } catch (error) {
      console.error('Error adding bill:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBill = async (billId) => {
    if (!window.confirm('Delete this shared bill?')) return;
    
    try {
      const response = await fetch(`${API}/households/${householdId}/bills/${billId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Error deleting bill:', error);
    }
  };

  const totalBills = bills.reduce((sum, b) => sum + b.amount, 0);
  const billsByCategory = bills.reduce((acc, bill) => {
    const cat = bill.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(bill);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to={`/household/${householdId}/dashboard`} className="text-gray-500 hover:text-gray-700 text-sm">
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          📝 Shared Bills - {household?.name}
        </h1>
        <p className="text-gray-600">Manage bills shared by the household</p>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-red-100 text-sm">Total Monthly Bills</p>
            <p className="text-4xl font-bold">${totalBills.toLocaleString()}</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition-colors"
          >
            + Add Bill
          </button>
        </div>
      </div>

      {/* Add Bill Form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4">Add Shared Bill</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Bill Name</label>
              <input
                type="text"
                value={newBill.name}
                onChange={(e) => setNewBill({ ...newBill, name: e.target.value })}
                placeholder="e.g., Rent, Electric, Internet"
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Amount</label>
              <input
                type="number"
                value={newBill.amount}
                onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })}
                placeholder="Enter amount"
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
              <select
                value={newBill.category}
                onChange={(e) => setNewBill({ ...newBill, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Due Day</label>
              <input
                type="number"
                min="1"
                max="31"
                value={newBill.due_day}
                onChange={(e) => setNewBill({ ...newBill, due_day: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleAddBill}
              disabled={saving || !newBill.name || !newBill.amount}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Adding...' : 'Add Bill'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bills by Category */}
      {Object.keys(billsByCategory).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(billsByCategory).map(([category, categoryBills]) => (
            <div key={category} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">{category}</h3>
                  <span className="text-sm text-gray-500">
                    ${categoryBills.reduce((s, b) => s + b.amount, 0).toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {categoryBills.map((bill) => (
                  <div key={bill.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-gray-900">{bill.name}</p>
                      <p className="text-sm text-gray-500">Due day {bill.due_day} • {bill.frequency}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-red-600">${bill.amount.toLocaleString()}</span>
                      <button
                        onClick={() => handleDeleteBill(bill.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <div className="text-5xl mb-4">📝</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">No shared bills yet</h2>
          <p className="text-gray-600 mb-4">Add your first shared bill to start tracking</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
          >
            Add First Bill
          </button>
        </div>
      )}
    </div>
  );
}

export default HouseholdBills;
